/*
  Drop-in meal builder reset logic for Shopify meal-plan pages.

  What it does:
  - Detects meal-tab changes (10 / 20 / 30)
  - Detects downward changes like 30 -> 10
  - Resets the in-memory selection array/state
  - Reloads only .option_grid sections via Shopify's Section Rendering API
  - Keeps event handling working after dynamic section replacement

  Notes:
  - This script derives section ids from either:
      1. .option_grid[data-section-id]
      2. the default Shopify wrapper id: #shopify-section-{{ section.id }}
  - If your theme can be updated, adding data-section-id="{{ section.id }}" to
    the .option_grid section root makes the lookup more explicit.
*/

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    if (!this.input) return;

    this.input.addEventListener('change', this.onInputChange.bind(this));

    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  connectedCallback() {
    this.resetInputValue();
    this.validateQtyRules();
  }

  resetInputValue() {
    if (!this.input || this.input.value) return;
    this.input.value = this.input.min || 0;
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();

    if (!this.input) return;

    const button = event.target.closest('button');
    if (!button) return;

    const previousValue = this.input.value;

    if (button.name === 'plus') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  validateQtyRules() {
    if (!this.input) return;

    const value = parseInt(this.input.value, 10);
    const min = parseInt(this.input.min || 0, 10);
    const max = parseInt(this.input.max || Infinity, 10);

    const minusButton = this.querySelector("[name='minus']");
    const plusButton = this.querySelector("[name='plus']");

    if (minusButton) {
      minusButton.classList.toggle('disabled', value <= min);
    }

    if (plusButton) {
      plusButton.classList.toggle('disabled', value >= max);
    }

    const card = this.closest('.meal-content');
    if (!card) return;

    card.classList.toggle('has-qty', value > 0);
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

(() => {
  if (window.__buildYourMealInitialized) return;
  window.__buildYourMealInitialized = true;

  const state = {
    maxMeals: 10,
    selectedMeals: 0,
    items: {}
  };

  const childId = `child_${Math.floor(10000 + Math.random() * 90000)}`;
  let dom = {};
  let sectionReloadInFlight = false;

  const isWarehouseSelected = () => !!(document.body.dataset.selectedWarehouse || '').trim();

  const parsePrice = (priceStr) => Number((priceStr || '').replace(/[^0-9.]/g, '')) || 0;

  const cacheDom = () => {
    const builder = document.querySelector('[data-meal-builder]');

    dom = {
      builder,
      slotsWrap: builder ? builder.querySelector('[data-slots]') : null,
      progressText: builder ? builder.querySelector('[data-progress]') : null,
      ctaBtn: builder ? builder.querySelector('.meal-builder__cta') : null
    };
  };

  const getPlusButtons = () => Array.from(document.querySelectorAll('.quantity__button.plus'));

  const setMealTabState = (selectedMeals) => {
    document.querySelectorAll('.meal-tab').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.meals === String(selectedMeals));
    });
  };

  const renderSlots = () => {
    if (!dom.slotsWrap) return;

    dom.slotsWrap.innerHTML = '';
    const expandedItems = [];

    Object.keys(state.items).forEach((id) => {
      for (let index = 0; index < state.items[id].qty; index += 1) {
        expandedItems.push(state.items[id]);
      }
    });

    for (let slotIndex = 0; slotIndex < state.maxMeals; slotIndex += 1) {
      const slot = document.createElement('div');
      slot.className = 'meal-slot';

      if (expandedItems[slotIndex]) {
        const img = document.createElement('img');
        img.src = expandedItems[slotIndex].image;
        img.alt = expandedItems[slotIndex].title || 'Selected meal';
        slot.appendChild(img);
      } else {
        slot.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" aria-hidden="true">
            <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
          </svg>
        `;
      }

      dom.slotsWrap.appendChild(slot);
    }
  };

  const updateUI = () => {
    if (dom.progressText) {
      dom.progressText.textContent = `${state.selectedMeals}/${state.maxMeals} Meals`;
    }

    if (dom.ctaBtn) {
      const isComplete = state.selectedMeals === state.maxMeals && state.maxMeals > 0;
      const canCheckout = isComplete && isWarehouseSelected();
      dom.ctaBtn.classList.toggle('is-active', canCheckout);

      if (canCheckout) {
        dom.ctaBtn.removeAttribute('disabled');
      } else {
        dom.ctaBtn.setAttribute('disabled', 'disabled');
      }
    }

    const isMaxed = state.selectedMeals >= state.maxMeals;
    getPlusButtons().forEach((button) => {
      if (isMaxed) {
        button.setAttribute('disabled', true);
        button.classList.add('is-disabled');
      } else {
        button.removeAttribute('disabled');
        button.classList.remove('is-disabled');
      }
    });
  };

  const updateTotalPrice = () => {
    let total = 0;

    Object.keys(state.items).forEach((id) => {
      const item = state.items[id];
      const card = document.querySelector(`[data-id="${id}"]`);
      if (!card) return;

      const priceInput = card.querySelector('.option_price');
      total += parsePrice(priceInput ? priceInput.value : '') * item.qty;
    });

    const priceEl = document.querySelector('.total_meal_count .meal-price');
    if (priceEl) {
      priceEl.textContent = `$${total.toFixed(2)}`;
    }
  };

  const updateMealProperties = () => {
    const form = document.querySelector('.meal_builder_bottom .shopify-product-form');
    if (!form) return;

    const inputContainer = form.querySelector('.meal-properties');
    if (!inputContainer) return;

    inputContainer.innerHTML = '';

    if (Object.keys(state.items).length) {
      const mainChildInput = document.createElement('input');
      mainChildInput.type = 'hidden';
      mainChildInput.name = 'properties[_child_id]';
      mainChildInput.value = childId;
      inputContainer.appendChild(mainChildInput);
    }

    let index = 1;

    Object.keys(state.items).forEach((id) => {
      const item = state.items[id];

      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = `items[${index}][id]`;
      idInput.value = id;

      const qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = `items[${index}][quantity]`;
      qtyInput.value = item.qty;

      const childInput = document.createElement('input');
      childInput.type = 'hidden';
      childInput.name = `items[${index}][properties][_child_id]`;
      childInput.value = childId;

      inputContainer.appendChild(idInput);
      inputContainer.appendChild(qtyInput);
      inputContainer.appendChild(childInput);

      index += 1;
    });
  };

  // ---- Bundle AJAX Cart Submission ----
  // Replaces the legacy form-based submission that added a 0-price default variant.
  // The first selected product becomes the bundle parent; all remaining items are children.

  const generateBundleId = () =>
    `bundle_${Math.floor(100000 + Math.random() * 900000)}`;

  const getCartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const openCartDrawer = (sectionsPayload) => {
    // Dispatch Horizon's native 'cart:update' event (ThemeEvents.cartUpdate).
    // CartItemsComponent listens to this: if sections HTML is present for its
    // sectionId it calls morphSection() to patch the DOM in-place; otherwise it
    // falls back to sectionRenderer.renderSection() for a fresh fetch.
    // CartDrawerComponent also listens and calls showDialog() when auto-open is set.
    const cartUpdateEvent = new Event('cart:update', { bubbles: true });
    cartUpdateEvent.detail = {
      resource: null,
      sourceId: 'meal-builder',
      data: {
        source: 'meal-builder',
        sections: sectionsPayload || {}
      }
    };
    document.dispatchEvent(cartUpdateEvent);

    // Directly call open() on cart-drawer-component so the drawer always opens
    // regardless of whether the auto-open theme setting is enabled.
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  };

  const submitBundle = async () => {
    if (!isWarehouseSelected()) return;

    const itemEntries = Object.entries(state.items);
    if (!itemEntries.length) return;

    const bundleId = generateBundleId();

    // --- Build cart items: first = parent, rest = children ---
    const cartItems = [];
    let parentSet = false;

    itemEntries.forEach(([id, item]) => {
      const properties = {};

      if (!parentSet) {
        // First selected product is the bundle parent
        properties['_parent_id'] = bundleId;
        parentSet = true;
      } else {
        // All subsequent selections are bundle children
        properties['_child_id'] = bundleId;
      }

      cartItems.push({
        id: parseInt(id, 10),
        quantity: item.qty,
        properties
      });
    });

    // --- Disable CTA while in-flight ---
    if (dom.ctaBtn) {
      dom.ctaBtn.setAttribute('disabled', 'disabled');
      dom.ctaBtn.classList.remove('is-active');
      dom.ctaBtn.classList.add('is-loading');
    }

    // Collect section IDs from cart-items-component elements exactly as
    // product-form.js does � ensures Shopify renders the right sections HTML
    // so CartItemsComponent can morph the cart drawer content in one round-trip.
    const cartSectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) cartSectionIds.push(el.dataset.sectionId);
    });

    try {
      const response = await fetch(`${getCartRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: cartItems,
          sections: cartSectionIds.length ? cartSectionIds : ['cart-drawer', 'cart-notification']
        })
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.description || `Cart add failed (${response.status})`);
      }

      const data = await response.json();

      // Open cart drawer with fresh section HTML when available
      openCartDrawer(data.sections);

      // Reset builder back to initial state
      resetSelectionState();
      resetVisibleQuantities();
      refreshBuilderUI();
    } catch (error) {
      console.error('[MealBuilder] submitBundle error:', error);

      // Re-enable CTA so user can retry
      if (dom.ctaBtn) {
        dom.ctaBtn.removeAttribute('disabled');
        dom.ctaBtn.classList.add('is-active');
      }
    } finally {
      if (dom.ctaBtn) {
        dom.ctaBtn.classList.remove('is-loading');
      }
    }
  };

  // ---- End Bundle AJAX Cart Submission ----

  const refreshBuilderUI = () => {
    renderSlots();
    updateUI();
    updateMealProperties();
    updateTotalPrice();
  };

  const resetSelectionState = () => {
    state.selectedMeals = 0;
    state.items = {};
  };

  const resetVisibleQuantities = () => {
    document.querySelectorAll('.option_grid quantity-input').forEach((quantityInput) => {
      const input = quantityInput.querySelector('.quantity__input');
      const content = quantityInput.closest('.meal-content');
      const minusButton = quantityInput.querySelector("[name='minus']");
      const plusButton = quantityInput.querySelector("[name='plus']");

      if (input) {
        input.value = input.min || 0;
      }

      if (content) {
        content.classList.remove('has-qty');
      }

      if (minusButton) {
        minusButton.classList.add('disabled');
      }

      if (plusButton) {
        plusButton.classList.remove('is-disabled');
        plusButton.removeAttribute('disabled');
      }
    });
  };

  const getOptionGridSectionIds = () => {
    const ids = new Set();

    document.querySelectorAll('.option_grid').forEach((section) => {
      const explicitId = section.dataset.sectionId;
      if (explicitId) {
        ids.add(explicitId);
        return;
      }

      const wrapper = section.closest('[id^="shopify-section-"]');
      if (wrapper) {
        ids.add(wrapper.id.replace('shopify-section-', ''));
      }
    });

    return Array.from(ids);
  };

  const replaceOptionGridFragments = (currentSection, nextSection) => {
    if (!currentSection || !nextSection) return;

    const selectorsToReplace = [
      '.filter-facets-section',
      '.meal-grid-wrapper',
      '.no-meals-message'
    ];

    selectorsToReplace.forEach((selector) => {
      const currentNode = currentSection.querySelector(selector);
      const nextNode = nextSection.querySelector(selector);

      if (currentNode && nextNode) {
        currentNode.replaceWith(nextNode);
      }
    });
  };

  const replaceOptionGridSectionsFromDocument = (sourceDocument) => {
    document.querySelectorAll('.option_grid').forEach((currentSection) => {
      const wrapper = currentSection.closest('[id^="shopify-section-"]');
      const sectionId = currentSection.dataset.sectionId || (wrapper ? wrapper.id.replace('shopify-section-', '') : '');
      if (!sectionId) return;

      const nextSection =
        sourceDocument.querySelector(`.option_grid[data-section-id="${sectionId}"]`) ||
        sourceDocument.querySelector(`#shopify-section-${sectionId} .option_grid`);

      if (nextSection) {
        replaceOptionGridFragments(currentSection, nextSection);
      }
    });
  };

  const notifySectionReload = () => {
    const optionGrids = Array.from(document.querySelectorAll('.option_grid'));
    const facetsContainers = Array.from(document.querySelectorAll('.filter-facets-section'));

    document.dispatchEvent(
      new CustomEvent('option-grid:reloaded', {
        detail: {
          sections: optionGrids,
          facets: facetsContainers
        }
      })
    );

    document.dispatchEvent(
      new CustomEvent('facets:reloaded', {
        detail: {
          containers: facetsContainers
        }
      })
    );

    if (typeof window.initFacetsDetails === 'function') {
      facetsContainers.forEach((container) => {
        window.initFacetsDetails(container);
      });
    }
  };

  const reloadOptionGridSections = async () => {
    if (sectionReloadInFlight) return;

    const sectionIds = getOptionGridSectionIds();
    if (!sectionIds.length) return;

    sectionReloadInFlight = true;

    try {
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('sections', sectionIds.join(','));

      const response = await fetch(sectionUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Section rendering failed with status ${response.status}`);
      }

      const payload = await response.json();

      sectionIds.forEach((sectionId) => {
        if (typeof payload[sectionId] !== 'string') return;

        const currentSection =
          document.querySelector(`.option_grid[data-section-id="${sectionId}"]`) ||
          document.querySelector(`#shopify-section-${sectionId} .option_grid`);

        if (!currentSection) return;

        const parsedDocument = new DOMParser().parseFromString(payload[sectionId], 'text/html');
        const nextSection =
          parsedDocument.querySelector(`.option_grid[data-section-id="${sectionId}"]`) ||
          parsedDocument.querySelector('.option_grid');

        if (!nextSection) return;

        replaceOptionGridFragments(currentSection, nextSection);
      });
    } catch (error) {
      console.warn('Falling back to page markup reload for option grid sections.', error);

      const fallbackResponse = await fetch(window.location.href, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (fallbackResponse.ok) {
        const markup = await fallbackResponse.text();
        const sourceDocument = new DOMParser().parseFromString(markup, 'text/html');
        replaceOptionGridSectionsFromDocument(sourceDocument);
      }
    } finally {
      cacheDom();
      refreshBuilderUI();
      notifySectionReload();
      sectionReloadInFlight = false;
    }
  };

  const trimSelectionsToMax = () => {
    if (state.selectedMeals <= state.maxMeals) return;

    let extra = state.selectedMeals - state.maxMeals;

    Object.keys(state.items).forEach((id) => {
      while (state.items[id] && state.items[id].qty > 0 && extra > 0) {
        state.items[id].qty -= 1;
        state.selectedMeals -= 1;
        extra -= 1;

        if (state.items[id].qty <= 0) {
          delete state.items[id];
        }
      }
    });
  };

  const handleMealTabChange = async (tab) => {
    const meals = parseInt(tab.dataset.meals || '', 10);
    if (!meals || Number.isNaN(meals)) return;

    const previousMaxMeals = state.maxMeals;
    state.maxMeals = meals;
    setMealTabState(meals);

    if (meals < previousMaxMeals) {
      resetSelectionState();
      resetVisibleQuantities();
      refreshBuilderUI();
      await reloadOptionGridSections();
      return;
    }

    trimSelectionsToMax();
    refreshBuilderUI();
  };

  const handlePlusClick = (button) => {
    const card = button.closest('[data-meal-card]');
    if (!card || state.selectedMeals >= state.maxMeals) return;

    const id = card.dataset.id;
    if (!id) return;

    const title = card.dataset.title || '';
    const image = card.dataset.image || '';

    if (!state.items[id]) {
      state.items[id] = { qty: 1, image, title };
    } else {
      state.items[id].qty += 1;
    }

    state.selectedMeals += 1;
    refreshBuilderUI();
  };

  const handleMinusClick = (button) => {
    const card = button.closest('[data-meal-card]');
    if (!card) return;

    const id = card.dataset.id;
    if (!id || !state.items[id]) return;

    state.items[id].qty -= 1;
    state.selectedMeals -= 1;

    if (state.items[id].qty <= 0) {
      delete state.items[id];
    }

    refreshBuilderUI();
  };

  const bindEvents = () => {
    // Intercept the bundle builder form submit in capture phase so Horizon's
    // product-form.js never sees it.
    document.addEventListener(
      'submit',
      (event) => {
        if (!event.target.closest('.meal_builder_bottom')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        submitBundle();
      },
      true
    );

    document.addEventListener('click', (event) => {
      const addToCartTrigger = event.target.closest('.add-to-cart-btn');
      if (addToCartTrigger) {
        if (!isWarehouseSelected()) return;
        const wrapper = addToCartTrigger.closest('.add_to_cart_wrapper');
        const plusBtn = wrapper ? wrapper.querySelector('.quantity__button.plus') : null;
        if (plusBtn && !plusBtn.disabled) {
          plusBtn.click();
        }
        return;
      }

      const mealTab = event.target.closest('.meal-tab');
      if (mealTab) {
        handleMealTabChange(mealTab);
        return;
      }

      const plusButton = event.target.closest('.quantity__button.plus');
      if (plusButton) {
        if (!isWarehouseSelected()) return;
        handlePlusClick(plusButton);
        return;
      }

      const minusButton = event.target.closest('.quantity__button.minus');
      if (minusButton) {
        handleMinusClick(minusButton);
      }
    });

    window.addEventListener('scroll', () => {
      if (!dom.builder) return;

      if (window.scrollY > 800) {
        dom.builder.classList.add('is-visible');
      } else {
        dom.builder.classList.remove('is-visible');
      }
    });

    document.addEventListener('warehouse:changed', async () => {
      // Reset selections — available meals differ by warehouse
      resetSelectionState();
      resetVisibleQuantities();
      refreshBuilderUI();
      // Reload option grid sections via Section Rendering API
      // (same flow as a downward meal-tab change)
      await reloadOptionGridSections();
    });
  };

  const initialize = () => {
    cacheDom();

    const activeTab = document.querySelector('.meal-tab.is-active') || document.querySelector('.meal-tab');
    if (activeTab) {
      const activeMeals = parseInt(activeTab.dataset.meals || '', 10);
      if (activeMeals && !Number.isNaN(activeMeals)) {
        state.maxMeals = activeMeals;
      }
      setMealTabState(state.maxMeals);
    }

    refreshBuilderUI();
    bindEvents();
    // window.setInterval(updateCartCount, 5);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
